import { Box, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { useContext, useEffect, useState } from 'react';

import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { ProgramNames } from '@/models/Program';
import { useApplicationDeadline } from '@/hooks/useApplicationDeadline';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { UsersContext } from '@/Contexts/UsersContext';
import { getStateName } from '@/utils/stateUtil';

function DeadlineBanner() {
  const theme = useTheme();
  const { selectedProgramId } = useContext(SelectedProgramContext);
  const { partnerInfo: currentPartnerInfo } = useContext(SelectedPartnerContext);
  const { applicationCycle, partnerInfo: currentApplicationPartnerInfo } =
    useContext(CurrentApplicationContext);
  const { userCurrent } = useContext(UsersContext);
  const { allStates } = useContext(ConfigContext);
  const { translatedText } = useContext(LocaleContext);
  const { lookupDeadline } = useApplicationDeadline();

  const [deadline, setDeadline] = useState<string | null | undefined>(undefined);

  const reviewerAssignment = userCurrent.reviewerAssignments?.[0];

  const safeLookupDeadline = lookupDeadline;

  const currentStateId =
    currentApplicationPartnerInfo?.stateId ??
    applicationCycle?.stateId ??
    currentPartnerInfo?.stateId ??
    reviewerAssignment?.stateId ??
    null;

  const stateName = getStateName(allStates, currentStateId);
  const deadlineLabel =
    stateName != null
      ? `${stateName} ${translatedText.deadlineBanner.stateReviewDeadlineText}`
      : translatedText.deadlineBanner.deadlineText;

  function formatDeadline(dateString: string): string {
    return dayjs(dateString).format('LL');
  }

  useEffect(() => {
    const currentProgramId: ProgramNames | null =
      selectedProgramId != null && selectedProgramId !== ProgramNames._
        ? selectedProgramId
        : (applicationCycle?.programId ??
          (reviewerAssignment != null ? (reviewerAssignment.programId ?? ProgramNames._) : null));

    if (currentStateId != null && currentProgramId != null) {
      const deadlineValue = safeLookupDeadline(currentStateId, currentProgramId);
      setDeadline(deadlineValue);
    } else {
      setDeadline(null);
    }
  }, [
    currentStateId,
    applicationCycle?.stateId,
    currentApplicationPartnerInfo?.stateId,
    currentPartnerInfo?.stateId,
    safeLookupDeadline,
    reviewerAssignment,
    selectedProgramId,
    applicationCycle?.programId,
  ]);

  return (
    deadline != null && (
      <Box
        display='flex'
        alignItems='center'
        sx={{
          background: theme.palette.forestShade.main,
          height: '32px',
          color: theme.palette.sand.main,
          gap: '4px',
          paddingLeft: '184px',
        }}
      >
        <svg width='6' height='6' viewBox='0 0 6 6' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <circle cx='3' cy='3' r='3' fill={theme.palette.desert.main} />
        </svg>

        <Typography variant='subtitle2'>
          {`${deadlineLabel}:  ${formatDeadline(deadline)}`}
        </Typography>
      </Box>
    )
  );
}

export default DeadlineBanner;
